import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Loader2, ArrowLeft, Check } from "lucide-react";
import { reGuide, dl } from "@/lib/docstate";

export default function DocstateGuide() {
  const { slug } = useParams();
  const [g, setG] = useState(null);
  const [missing, setMissing] = useState(false);

  useEffect(() => { window.scrollTo(0, 0); setG(null); setMissing(false); reGuide(slug).then(setG).catch(() => setMissing(true)); }, [slug]);

  if (missing) return <div className="ds-bg flex min-h-screen flex-col items-center justify-center gap-4 pt-16"><p className="ds-display text-xl text-[#F3E5AB]">Guide not found</p><Link to={dl("/area-guides")} className="ds-outline-btn px-5 py-2.5 text-sm">All guides</Link></div>;
  if (!g) return <div className="ds-bg flex min-h-screen items-center justify-center pt-16"><Loader2 className="h-8 w-8 animate-spin text-[#C9A24B]" /></div>;

  return (
    <div className="ds-bg min-h-screen pt-16" data-testid="docstate-guide">
      <section className="relative flex min-h-[55vh] items-end overflow-hidden">
        <img src={g.cover} alt={g.community} className="absolute inset-0 h-full w-full object-cover opacity-50" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0B0B0C] via-[#0B0B0C]/60 to-transparent" />
        <div className="relative mx-auto w-full max-w-5xl px-4 pb-10 sm:px-8">
          <p className="ds-eyebrow">Area Guide</p>
          <h1 className="ds-display mt-2 text-4xl font-medium text-white sm:text-5xl">{g.community}</h1>
          <p className="mt-3 max-w-2xl text-lg font-light text-[#C9BBB2]">{g.intro}</p>
        </div>
      </section>

      <div className="mx-auto max-w-5xl px-4 py-12 sm:px-8">
        <Link to={dl("/area-guides")} className="mb-8 inline-flex items-center gap-2 text-sm text-[#9D8D83] transition-colors hover:text-[#F3E5AB]"><ArrowLeft className="h-4 w-4" /> All guides</Link>

        {Array.isArray(g.stats) && g.stats.length > 0 && (
          <div className="grid grid-cols-2 gap-4 sm:grid-cols-3" data-testid="guide-stats">
            {g.stats.map((s, i) => (
              <div key={i} className="ds-card p-6 text-center">
                <p className="ds-display text-2xl text-[#F3E5AB]">{s.value}</p>
                <p className="mt-1 text-xs uppercase tracking-widest text-[#9D8D83]">{s.label}</p>
              </div>
            ))}
          </div>
        )}

        {g.body && (
          <div className="mt-10 space-y-5 text-[17px] font-light leading-relaxed text-[#C9BBB2]">
            {String(g.body).split("\n").filter((l) => l.trim()).map((p, i) => <p key={i}>{p}</p>)}
          </div>
        )}

        {Array.isArray(g.highlights) && g.highlights.length > 0 && (
          <div className="mt-10">
            <h2 className="ds-display text-2xl text-white">Why live here</h2>
            <ul className="mt-5 grid gap-3 sm:grid-cols-2">
              {g.highlights.map((h, i) => (
                <li key={i} className="flex items-start gap-2 text-sm text-[#C9BBB2]"><Check className="mt-0.5 h-4 w-4 shrink-0 text-[#C9A24B]" /> {h}</li>
              ))}
            </ul>
          </div>
        )}

        <div className="mt-12 flex flex-wrap items-center justify-between gap-4 rounded-2xl border border-[#C9A24B]/20 bg-[#141317] p-6">
          <div>
            <p className="ds-display text-xl text-white">{g.listing_count || 0} properties in {g.community}</p>
            <p className="text-sm font-light text-[#9D8D83]">Browse everything we have available in this community.</p>
          </div>
          <Link to={dl(`/properties?community=${encodeURIComponent(g.community)}`)} className="ds-gold-btn px-6 py-3 text-sm" data-testid="guide-view-listings">View listings</Link>
        </div>
      </div>
    </div>
  );
}
